import { TextField, FormHelperText, Autocomplete } from '@mui/material'
import { Vendor } from 'api/vendors'
import EditRow from 'components/DocumentDetail/Editor/EditRow'
import { HEADER_BILL_FROM_KEYS } from 'entities/DocumentsInvoiceBase'
import { DocumentInvoiceV2 } from 'entities/DocumentsInvoiceV2'
import { useHighlightContext } from 'hooks/useHighlightContext'
import { useCallback, useState, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { extractError } from 'utils/extractError'

const VENDOR_CODE_NAME = 'headerBillFrom.vendorCode'

type Props = {
  name: string
  defaultValue: string
  label: string
  compProps: any
  style: any
  docConfig: DocumentInvoiceV2['config']
  document: DocumentInvoiceV2
  vendors: Vendor[]
}

const VendorNameRow = ({
  name,
  defaultValue,
  label,
  compProps,
  style,
  docConfig,
  vendors,
}: Props) => {
  const { update: updateHL } = useHighlightContext()
  const vendorOptions = useMemo(
    () =>
      vendors.map((vendor) => ({
        ...vendor,
        label: `${vendor.vendor_name} (${vendor.vendor_code})`,
        billFromCompanyName: vendor.vendor_name,
        vendorCode: vendor.vendor_code,
        billFromPersonName: `${vendor.contact_last_name} ${vendor.contact_first_name}`.trim(),
        billFromPostalCode: vendor.postal_code,
        billFromAddress: ['state', 'city', 'address1', 'address2', 'address3']
          .map((key) => vendor[key] || '')
          .join(' ')
          .trim(),
        billFromTel: vendor.contact_phone_number,
        billFromAddressCode: vendor.address_code,
        billFromCompanyId: vendor.tax_id,
      })),
    [vendors],
  )

  const [txtVal, setTxtVal] = useState(defaultValue)
  const {
    setValue,
    formState: { errors },
    register,
  } = useFormContext()
  const vendorCodeError = extractError(errors, VENDOR_CODE_NAME)

  const updateForm = useCallback(
    (data = {}) => {
      HEADER_BILL_FROM_KEYS.filter((key) => docConfig?.headerBillFrom?.[key]).forEach((k) => {
        const options = k === 'vendorCode' ? { shouldDirty: true, shouldValidate: true } : undefined
        k === 'billFromCompanyName' && setTxtVal(data[k])
        setValue(`headerBillFrom.${k}`, data[k] || '', options)
      })
    },
    [setValue, docConfig, setTxtVal],
  )

  const onChange = (event, data, reason) => {
    if (reason === 'clear') {
      updateForm()
    } else if (data) {
      updateForm(data)
    }
  }

  return (
    <EditRow label={label} style={style} required>
      <Autocomplete
        autoComplete
        value={txtVal}
        options={vendorOptions}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            margin="dense"
            size="small"
            error={Boolean(vendorCodeError)}
            inputProps={{
              ...params.inputProps,
              autoComplete: 'new-password', // disable native autocomplete */}
            }}
          />
        )}
        onChange={onChange}
        noOptionsText={'取引先情報が存在しません'}
        onFocus={() => updateHL(name)}
        onBlur={() => updateHL(null)}
        {...compProps}
      />
      <input type="hidden" {...register(name)} />
      {vendorCodeError && (
        <FormHelperText error>{vendorCodeError?.message?.toString()}</FormHelperText>
      )}
    </EditRow>
  )
}

export default VendorNameRow
